<template>
    <v-menu v-model="menu" :close-on-content-click="false">
        <template v-slot:activator="{props}">
            <v-text-field
                v-model="date"
                :label="label"
                :disabled="disabled"
                :clearable="clearable"
                bg-color="white"
                prepend-inner-icon="mdi-calendar"
                :rules="rules"
                :persistent-placeholder="persistentPlaceholder"
                :type="textFieldType"
                :min="min"
                :max="max"
                :density="density"
                :color="color"
                :hint="hint"
                :hide-details="hideDetails"
                variant="outlined"
                :class="contentClass"
                required
                :width="width"
                v-bind="props"
                @update:model-value="change"
                @update:focused="focused = $event"
                @click="$event.preventDefault()"
            />
        </template>
        <v-date-picker
            v-model="dateObj"
            :class="{'year-active': isYearActive, 'year-picker': activePicker === 'year'}"
            :min="min"
            :max="max"
            hide-header
            :view-mode="activePicker"
            @update:model-value="menu = false"
        />
    </v-menu>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {capitalizeTheFirstLetterOfEachWord} from '@/utils/helpers'

    @Component
    export class DatePicker extends Vue {
        @VModel({type: String}) date
        @Prop({default: '1900-01-01', type: String}) min
        @Prop({default: '2100-01-01', type: String}) max
        @Prop() label
        @Prop({default: false, type: Boolean}) disabled
        @Prop({default: false, type: [Boolean, String]}) hideDetails
        @Prop({default: false, type: Boolean}) dense
        @Prop({default: 'comfortable', type: String}) density
        @Prop({default: 'primary', type: String}) color
        @Prop({default: false, type: Boolean}) outlined
        @Prop({default: false, type: Boolean}) yearActivePicker
        @Prop({default: false, type: Boolean}) persistentPlaceholder
        @Prop({default: 'date'}) textFieldType
        @Prop({default: () => []}) rules
        @Prop() contentClass
        @Prop({default: true, type: Boolean}) clearable
        @Prop() width

        menu = false
        isYearActive = false
        activePicker: 'month' | 'year' | 'months' = 'month'
        focused = false

        mounted() {
            if (this.yearActivePicker) {
                this.activePicker = 'year'
            }
        }

        // Needs to fix the top scroll issue when opening the year datepicker
        @Watch('menu')
        onMenuChange(menu) {
            if (menu && this.activePicker === 'year') {
                setTimeout(() => {
                    this.isYearActive = true
                }, 0)
            } else {
                this.isYearActive = false
            }
        }

        change(val) {
            const date = this.$dateFns.parseISO(val),
                min = this.$dateFns.parseISO(this.min),
                max = this.$dateFns.parseISO(this.max)
            if (this.$dateFns.isBefore(date, min) && !this.focused) {
                this.date = this.min
            } else if (this.$dateFns.isAfter(date, max)) {
                this.date = this.max
            }
        }

        get firstDayOfWeekByLocale() {
            return this.$dateFns.localeOptions[this.$i18n.locale].weekStartsOn
        }

        get hint() {
            const dateShortFormat = this.$filters.dateShortFormat
            if (this.max !== '2100-01-01' && this.min !== '1900-01-01') {
                return `${capitalizeTheFirstLetterOfEachWord(this.$t('from'))} ${dateShortFormat(
                    this.$dateFns.parseISO(this.min)
                )} ${this.$t('to')} ${dateShortFormat(this.$dateFns.parseISO(this.max))}`
            } else if (this.max !== '2100-01-01') {
                return `${capitalizeTheFirstLetterOfEachWord(this.$t('to'))} ${dateShortFormat(
                    this.$dateFns.parseISO(this.max)
                )}`
            } else if (this.min && this.min !== '1900-01-01') {
                return `${capitalizeTheFirstLetterOfEachWord(this.$t('from'))} ${dateShortFormat(
                    this.$dateFns.parseISO(this.min)
                )}`
            }
            return ''
        }

        get dateObj() {
            return this.date ? this.$dateFns.parseISO(this.date) : null
        }

        set dateObj(date: Date) {
            this.date = this.$dateFns.format(date)
        }
    }

    export default DatePicker
</script>

<style scoped lang="scss">
    .year {
        &-picker :deep(button) {
            display: none;
        }
        &-active :deep(button) {
            display: block;
        }
    }
</style>
